import type { SupportedCountry } from "@App/utils/country";
import {
  type GermanNumberPlateFields,
  isValidNumberPlate,
  joinGermanNumberPlate,
  splitGermanNumberPlate,
} from "@App/utils/numberPlate";
import { t } from "i18next";
import { Component, createRef, type ReactNode, type RefObject } from "react";

/**
 * "fields" is the German three-part plate. "free" is a single input for every other
 * country — the backend parses both formats and infers the country itself, so there
 * is no need to model foreign plate layouts here.
 */
type NumberPlateInputMode = "fields" | "free";

type NumberPlateInputProps = {
  value?: string | null;
  validationMessage?: string;
  onChange: (numberPlate: string) => void;
  onBlur?: () => void;
  showNumberPlateSyncMessage?: boolean;
  disabled?: boolean;
  countryCode?: SupportedCountry;
};

/**
 * The three German fields only exist inside this component: a plate leaves it — and is
 * stored, sent and compared everywhere else — as a single string.
 */
type NumberPlateInputState = {
  fields: GermanNumberPlateFields;
  free: string;
  mode: NumberPlateInputMode;
  freeTextInvalid: boolean;
};

const emptyFields = (): GermanNumberPlateFields => ({ region: "", firstID: "", secondID: "" });

const modeFor = (countryCode?: SupportedCountry): NumberPlateInputMode =>
  countryCode === "AT" ? "free" : "fields";

export class NumberPlateInput extends Component<NumberPlateInputProps, NumberPlateInputState> {
  private firstRef: RefObject<HTMLInputElement>;
  private secondRef: RefObject<HTMLInputElement>;

  constructor(props: NumberPlateInputProps) {
    super(props);
    this.firstRef = createRef();
    this.secondRef = createRef();
    this.state = {
      fields: splitGermanNumberPlate(props.value) ?? emptyFields(),
      free: props.value ?? "",
      mode: modeFor(props.countryCode),
      freeTextInvalid: false,
    };
  }

  componentDidUpdate(previous: NumberPlateInputProps): void {
    if (previous.countryCode === this.props.countryCode) return;
    // "M-AB 1234" is a valid German plate and meaningless as an Austrian one, so the
    // value cannot survive a country switch.
    this.resetTo(modeFor(this.props.countryCode));
  }

  private resetTo(mode: NumberPlateInputMode): void {
    this.setState({ fields: emptyFields(), free: "", mode, freeTextInvalid: false });
    this.props.onChange("");
  }

  toggleMode = (): void => {
    this.resetTo(this.state.mode === "fields" ? "free" : "fields");
  };

  onFreeTextChange = (event): void => {
    const free = event.target.value.toUpperCase();
    this.setState({
      free,
      freeTextInvalid: free.trim().length > 0 && !isValidNumberPlate(free),
    });
    this.props.onChange(free.trim());
  };

  onChange = async (event) => {
    const fields = { ...this.state.fields };
    fields[event.target.name] = this.formatInput(event);
    await this.setState({ fields });

    this.props.onChange(joinGermanNumberPlate(this.state.fields));
  };

  formatInput = (event) => {
    let result = this.state.fields[event.target.name];

    if (event.target.name === "region") {
      const re = /^[a-zA-ZäöüÄÖÜß\b]+$/;
      if (event.target.value === "" || re.test(event.target.value)) {
        result = event.target.value.toUpperCase();
      }
    } else if (event.target.name === "firstID") {
      const re = /^[a-zA-ZäöüÄÖÜß\b]+$/;
      if (event.target.value === "" || re.test(event.target.value)) {
        result = event.target.value.toUpperCase();
      }
    } else if (event.target.name === "secondID") {
      const re = /^[0-9]{1,4}[eE]?$/;
      if (event.target.value === "" || re.test(event.target.value)) {
        result = event.target.value.toUpperCase();
      }
    }

    if (event.target.name === "region" && event.target.value.length === 3) {
      this.firstRef.current.focus();
    } else if (event.target.name === "firstID" && event.target.value.length === 2) {
      this.secondRef.current.focus();
    }
    return result;
  };

  private renderFields(): ReactNode {
    return (
      <>
        <input
          type="text"
          className="text-center text-[35px] leading-none col-span-3 border-0 border-b border-neutral-500"
          data-testid="region"
          name="region"
          placeholder="A"
          value={this.state.fields.region}
          onChange={this.onChange}
          maxLength={3}
        />
        <div className="col-span-1 flex flex-col gap-1 mx-auto">
          <span className="w-5 h-5 rounded-xl bg-neutral-100 mx-auto" />
          <span className="w-6 h-6 rounded-xl bg-neutral-100" />
        </div>
        <input
          type="text"
          className="text-center text-[35px] leading-none col-span-2 border-0 border-b border-neutral-500"
          name="firstID"
          placeholder="BC"
          data-testid="firstID"
          value={this.state.fields.firstID}
          onChange={this.onChange}
          ref={this.firstRef}
          maxLength={2}
        />
        <input
          type="text"
          className="text-center text-[35px] leading-none col-span-4 border-0 border-b border-neutral-500"
          name="secondID"
          placeholder="1234"
          data-testid="secondID"
          value={this.state.fields.secondID}
          onChange={this.onChange}
          onBlur={() => this.props.onBlur}
          ref={this.secondRef}
          maxLength={5}
        />
      </>
    );
  }

  private renderFreeText(): ReactNode {
    // No Plaketten circles: the two grey dots are German inspection badges.
    return (
      <input
        type="text"
        className="text-center text-[35px] leading-none col-span-10 border-0 border-b border-neutral-500"
        name="numberPlateFree"
        data-testid="numberPlateFree"
        placeholder={t<string>("numberPlateInput.free-placeholder")}
        value={this.state.free}
        onChange={this.onFreeTextChange}
        onBlur={() => this.props.onBlur}
        maxLength={15}
      />
    );
  }

  render(): ReactNode {
    const isFreeMode = this.state.mode === "free";

    return (
      <>
        <div className={"block md:flex gap-3 items-center"}>
          <fieldset
            disabled={this.props.disabled}
            className="grid grid-cols-10 gap-2 w-full md:w-full items-center bg-white border border-neutral-500 rounded-lg h-16 p-2 my-2"
          >
            {isFreeMode ? this.renderFreeText() : this.renderFields()}
          </fieldset>
          {!!this.props.showNumberPlateSyncMessage && (
            <div className={"w-full md:w-80 text-sm"}>
              <p>
                Ihre Fahrzeugdaten werden automatisch anhand des Kennzeichens an die Werkstatt
                übermittelt.
              </p>
            </div>
          )}
        </div>
        {/*
          The toggle only makes sense once a country is known: it exists for the case
          where the location country and the car's plate country differ. Flows that
          mount without a searched place (partner, fleet, vehicle return) pass no
          countryCode and keep rendering exactly what they render today.
        */}
        {this.props.countryCode !== undefined && (
          <button
            type="button"
            data-testid="numberPlateModeToggle"
            className="text-sm underline text-neutral-600"
            onClick={this.toggleMode}
            disabled={this.props.disabled}
          >
            {isFreeMode
              ? t<string>("numberPlateInput.toggle-to-german")
              : t<string>("numberPlateInput.toggle-to-foreign")}
          </button>
        )}
        {isFreeMode && this.state.freeTextInvalid && (
          <div className={"mb-3 text-sm text-error"} data-testid="numberPlateFreeError">
            {t<string>("numberPlateInput.invalid-format")}
          </div>
        )}
        {this.props.validationMessage && (
          <div className={"mb-3 text-sm text-error"}>{this.props.validationMessage}</div>
        )}
      </>
    );
  }
}
